// src/ui/notifications/engine.js
// MVP do motor de notificações (front-only): salva filtros, faz polling em /api/leiloes,
// detecta "novo compatível", "queda de preço" e "prazo chegando" p/ watchlist.

import { inbox } from "./bell";
import { toast } from "./toast";

/* ===== LocalStorage keys ===== */
const LS_FILTERS   = "ll.alertFilters";      // filtros salvos p/ alertas
const LS_SNAPSHOT  = "ll.lastSnapshot";      // { [id]: { lance, desconto, data_leilao, hash } }
const LS_WATCHLIST = "ll.watchlist";         // [id, id, ...]
const LS_PREFS     = "ll.prefs";             // { price:true, match:true, deadline24:true, deadline1:true }

/* ===== Helpers LS ===== */
const loadJSON = (k, f = null) => {
  try { const v = JSON.parse(localStorage.getItem(k) || "null"); return v ?? f; } catch { return f; }
};
const saveJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

/* ===== API pública de filtros ===== */
export function setAlertFilters(filters) {
  // formato: { estado?, cidade?, tipos?: string[], valorMin?, valorMax?, descontoMin? }
  saveJSON(LS_FILTERS, filters || {});
  // dica visual
  toast.push({ level: "success", title: "Alertas", msg: "Filtros de alertas salvos." });
}
export function getAlertFilters() {
  return loadJSON(LS_FILTERS, {});
}

/* ===== Preferências (liga/desliga gatilhos) ===== */
export function setAlertPrefs(prefs) {
  const prev = loadJSON(LS_PREFS, { price:true, match:true, deadline24:true, deadline1:true, updates:true });
  saveJSON(LS_PREFS, { ...prev, ...prefs });
}
function getPrefs() {
  return loadJSON(LS_PREFS, { price:true, match:true, deadline24:true, deadline1:true, updates:true });
}

/* ===== Utilidades ===== */
const toNum = (v) => Number(String(v).replace(/[^\d.,-]/g, "").replace(/\./g,"").replace(",","."));
function valorReferencia(item) {
  const lance     = toNum(item.valor_lance);
  const avaliacao = toNum(item.valor_avaliacao);
  const mercado   = toNum(item.valor_mercado);
  const p1        = toNum(item.primeira_praca);
  const mercadoInvalidoOuBaixo = mercado <= 0 || mercado < lance; // mercado vale abaixo da avaliação (fiduciária: avaliação = dívida)
  let ref = mercadoInvalidoOuBaixo ? (p1 > 0 ? p1 : (avaliacao > 0 ? avaliacao : lance)) : mercado;
  return Math.max(ref || 0, lance || 0);
}
function descontoPct(item) {
  const lance = toNum(item.valor_lance);
  const ref = valorReferencia(item);
  if (lance <= 0 || ref <= 0) return 0;
  return (1 - (lance / ref)) * 100;
}
function hashItem(item) {
  // hash simples de campos relevantes
  const s = [
    item.valor_lance, item.data_leilao,
    item.primeira_praca, item.segunda_praca,
    item.edital, item.matricula, item.imagem
  ].join("|");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return String(h);
}
function matchesFilters(item, f) {
  if (!f) return true;
  if (f.estado && String(item.estado || "").toUpperCase() !== String(f.estado).toUpperCase()) return false;
  if (f.cidade && String(item.cidade || "").toLowerCase() !== String(f.cidade).toLowerCase()) return false;
  if (f.tipos && f.tipos.length) {
    const t = String(item.tipo_imovel || "").toLowerCase();
    const ok = f.tipos.some(x => t.includes(String(x).toLowerCase()));
    if (!ok) return false;
  }
  if (f.valorMin && toNum(item.valor_lance) < Number(f.valorMin)) return false;
  if (f.valorMax && toNum(item.valor_lance) > Number(f.valorMax)) return false;
  if (f.descontoMin && descontoPct(item) < Number(f.descontoMin)) return false;
  return true;
}
function hoursUntil(dateIso) {
  if (!dateIso) return Infinity;
  const diffMs = new Date(dateIso).getTime() - Date.now();
  return diffMs / 36e5;
}

/* ===== Polling Engine ===== */
let _timer = null;
let _cleanup = null;

export function startNotificationsEngine({
  apiBase = process.env.REACT_APP_API_URL || "",
  intervalMs = 60000,
  limit = 300,
} = {}) {
  stopNotificationsEngine(); // garante único timer
  let busy = false, active = true, controller = null;
  const tick = async () => {
    if (!active || busy || document.hidden) return;
    busy = true;
    controller = new AbortController();
    try {
      const prefs = getPrefs();
      const filters = getAlertFilters();
      // pega os mais recentes; ajuste se seu endpoint exigir outros params
      const qs = new URLSearchParams({ page: "1", limit: String(limit), ordenarPor: "inclusao_nova" }).toString();
      const res = await fetch(`${apiBase}/api/leiloes?${qs}`, { signal: controller.signal });
      if (!res.ok) throw new Error("Falha nas notificações");
      const data = await res.json();
      if (!active || document.hidden || controller.signal.aborted) return;
      const list = Array.isArray(data?.leiloes) ? data.leiloes : (Array.isArray(data) ? data : []);

      const snapshot = loadJSON(LS_SNAPSHOT, {});
      const nowSnap = { ...snapshot };
      const watchlist = new Set(loadJSON(LS_WATCHLIST, []) || []);

      for (const it of list) {
        const id = it.id ?? it.leilaoId ?? it.codigo ?? null;
        if (!id) continue;

        const desc = Number(descontoPct(it).toFixed(2));
        const lance = toNum(it.valor_lance);
        const dataLeilao = it.data_leilao || it.primeira_praca_data || it.segunda_praca_data || null;
        const h = hashItem(it);

        const prev = snapshot[id];
        nowSnap[id] = { lance, desconto: desc, data_leilao: dataLeilao, hash: h };

        // 1) NOVO compatível (só se prefs.match)
        if (getTruthy(prefs.match) && !prev && matchesFilters(it, filters)) {
          inbox.push({
            level: "info",
            title: `${Math.round(desc)}% OFF · ${it.tipo_imovel || "Imóvel"} · ${it.cidade || ""}-${(it.estado || "").toUpperCase()}`,
            message: it.titulo || "Novo leilão compatível com seu perfil",
            ctaUrl: `/leilao/${id}`,
            entity: { kind: "leilao", id }
          });
        }

        // 2) QUEDA de preço / desconto melhorou (prefs.price)
        if (getTruthy(prefs.price) && prev) {
          const delta = (desc - Number(prev.desconto || 0));
          if (delta >= 5) { // limiar de 5 p.p. no desconto
            inbox.push({
              level: "success",
              title: `Desconto ↑ ${Math.round(desc)}% (${it.cidade || ""}-${(it.estado || "").toUpperCase()})`,
              message: `Lance caiu para ${formatBRL(lance)} (Δ +${delta.toFixed(1)} p.p.).`,
              ctaUrl: `/leilao/${id}`,
              entity: { kind: "leilao", id }
            });
            // toast só para eventos mais fortes
            toast.push({ level:"success", title:"Queda de preço", msg:`${(it.tipo_imovel||"Imóvel")} agora ${Math.round(desc)}% OFF` });
          }
        }

        // 3) PRAZO chegando p/ itens da watchlist (prefs.deadline*)
        if (watchlist.has(id) && dataLeilao) {
          const hLeft = hoursUntil(dataLeilao);
          if (getTruthy(prefs.deadline1) && hLeft <= 1 && (prev?.deadline1fired !== true)) {
            inbox.push({
              level: "warning",
              title: "Encerramento em ~1h",
              message: it.titulo || "Leilão na sua lista está para encerrar",
              ctaUrl: `/leilao/${id}`,
              entity: { kind: "leilao", id }
            });
            toast.push({ level:"warning", title:"Atenção", msg:"Leilão da sua lista encerra em ~1h" });
            nowSnap[id].deadline1fired = true;
          } else if (getTruthy(prefs.deadline24) && hLeft <= 24 && (prev?.deadline24fired !== true)) {
            inbox.push({
              level: "info",
              title: "Encerramento em 24h",
              message: it.titulo || "Leilão da sua lista encerra amanhã",
              ctaUrl: `/leilao/${id}`,
              entity: { kind: "leilao", id }
            });
            nowSnap[id].deadline24fired = true;
          }
        }

        // 4) ALTERAÇÃO relevante (hash mudou)
        if (getTruthy(prefs.updates) && prev && prev.hash && prev.hash !== h) {
          inbox.push({
            level: "info",
            title: "Leilão atualizado",
            message: it.titulo || "Campos alterados (data/fotos/edital)",
            ctaUrl: `/leilao/${id}`,
            entity: { kind: "leilao", id }
          });
        }
      }

      saveJSON(LS_SNAPSHOT, nowSnap);
    } catch (e) {
      // erro silencioso + toast único opcional
      // console.warn("engine error", e);
    } finally {
      busy = false;
      if (active && !document.hidden && controller?.signal.aborted) tick();
    }
  };

  const visibilityChanged = () => {
    if (document.hidden) controller?.abort();
    else tick();
  };
  document.addEventListener('visibilitychange', visibilityChanged);
  _cleanup = () => {
    active = false;
    controller?.abort();
    document.removeEventListener('visibilitychange', visibilityChanged);
  };
  tick(); // roda já na entrada
  _timer = setInterval(tick, Math.max(15000, intervalMs));
}

export function stopNotificationsEngine() {
  if (_timer) clearInterval(_timer);
  _timer = null;
  _cleanup?.();
  _cleanup = null;
}

/* ===== Utils visuais ===== */
function getTruthy(v) { return v === undefined ? true : !!v; }
function formatBRL(n) {
  return Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

if (typeof window !== "undefined") {
  window.llEngine = {
    setAlertFilters,
    getAlertFilters,
    setAlertPrefs,
    start: startNotificationsEngine,
    stop: stopNotificationsEngine,
  };
}